import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { defineMessages, useIntl } from "react-intl";

import Email from "@mapmycustomers/shared/types/email/Email";
import EmailEntity from "@mapmycustomers/shared/types/email/EmailEntity";
import { Activity, EntitiesSupportedByEmailFeature } from "@mapmycustomers/shared/types/entity";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import { Button } from "@mapmycustomers/ui";

import EmailLink from "@app/component/email/EmailLink";
import { fetchSentEmailEntities } from "@app/store/email/actions";
import getSentEmailRecipients, { SentEmailRecipient } from "@app/util/email/getSentEmailRecipients";
import normalizeEmail from "@app/util/email/normalizeEmail";

import styles from "./SentEmailRecipients.module.scss";

// A send can carry up to a hundred recipients, so show a readable first page and keep
// the rest one click away rather than burying the subject and body.
const MAX_VISIBLE_RECIPIENTS = 5;

const messages = defineMessages({
  showLess: {
    id: "email.sentEmailRecipients.showLess",
    defaultMessage: "Show less",
    description: "Collapses the recipient list of a sent email back to the first few",
  },
  showMore: {
    id: "email.sentEmailRecipients.showMore",
    defaultMessage: "+ {count} more",
    description: "Reveals the recipients of a sent email hidden past the visible cap",
  },
  to: {
    id: "email.sentEmailRecipients.to",
    defaultMessage: "To",
    description: "Email recipients - to",
  },
});

const getEmailRecipientEntity = (
  emailRecipient: SentEmailRecipient,
  { emailEntities, inaccessibleEmailEntities }: EmailData
) => {
  const normalizedRecipientEmail = normalizeEmail(emailRecipient.email);

  const emailEntity = emailEntities.find(
    (emailEntity) => normalizeEmail(emailEntity.email) === normalizedRecipientEmail
  );

  if (emailEntity) {
    return emailEntity;
  }

  const inaccessibleEmailEntity = inaccessibleEmailEntities.find(
    (emailEntity) => normalizeEmail(emailEntity.email) === normalizedRecipientEmail
  );

  if (inaccessibleEmailEntity) {
    return null;
  }

  return undefined;
};

interface EmailData {
  emailEntities: EntitiesSupportedByEmailFeature[];
  inaccessibleEmailEntities: EmailEntity[];
}

interface SentEmailRecipientsProps {
  activity: Activity;
  allowSendViaEmailClient?: boolean;
  allowSendViaMmcEmail?: boolean;
}

const SentEmailRecipients = ({
  activity,
  allowSendViaEmailClient,
  allowSendViaMmcEmail,
  onSentEmailEntitiesFetch,
}: SentEmailRecipientsProps & typeof mapDispatchToProps) => {
  const intl = useIntl();

  const recipients = useMemo(() => getSentEmailRecipients(activity), [activity]);
  // Each recipient of a merged send is addressed on its own log, and entities are fetched
  // per log, so the cache is keyed by log rather than held as a single bucket.
  const [emailDataById, setEmailDataById] = useState<Record<Email["id"], EmailData>>({});
  const [expanded, setExpanded] = useState(false);

  const recipientsKey = recipients.map(({ emailId }) => emailId).join(",");
  useEffect(() => {
    setEmailDataById({});
    setExpanded(false);
  }, [recipientsKey]);

  const fetchEmailRecipientEntity = useDynamicCallback((emailRecipient: SentEmailRecipient) => {
    return new Promise<EntitiesSupportedByEmailFeature | null | undefined>((resolve, reject) => {
      const emailData = emailDataById[emailRecipient.emailId];
      if (emailData) {
        resolve(getEmailRecipientEntity(emailRecipient, emailData));
        return;
      }

      onSentEmailEntitiesFetch({
        callback: ({ emailEntities, inaccessibleEmailEntities }) => {
          const emailData: EmailData = { emailEntities, inaccessibleEmailEntities };
          setEmailDataById((previousEmailDataById) => ({
            ...previousEmailDataById,
            [emailRecipient.emailId]: emailData,
          }));
          resolve(getEmailRecipientEntity(emailRecipient, emailData));
        },
        emailId: emailRecipient.emailId,
        failureCallback: () => {
          reject();
        },
      });
    });
  });

  const handleToggleExpanded = useCallback(() => setExpanded((expanded) => !expanded), []);

  const visibleRecipients =
    expanded || recipients.length <= MAX_VISIBLE_RECIPIENTS
      ? recipients
      : recipients.slice(0, MAX_VISIBLE_RECIPIENTS);
  const hiddenCount = recipients.length - visibleRecipients.length;

  // Every recipient of this send failed, so there is nobody to list. Drop the whole
  // section rather than leaving a "To" label with nothing under it.
  if (recipients.length === 0) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.to}>{intl.formatMessage(messages.to)}</div>
      <div>
        {visibleRecipients.map((recipient) => (
          <div className={styles.recipient} key={`${recipient.email} ${recipient.name}`}>
            <span>{recipient.name}</span>
            <span>
              (
              <EmailLink
                allowSendViaEmailClient={allowSendViaEmailClient}
                allowSendViaMmcEmail={allowSendViaMmcEmail}
                className={styles.email}
                email={recipient.email}
                onFetchEntity={() => fetchEmailRecipientEntity(recipient)}
                showIcon={false}
              />
              )
            </span>
          </div>
        ))}
        {hiddenCount > 0 || expanded ? (
          <Button className={styles.toggle} onClick={handleToggleExpanded} type="text">
            {expanded
              ? intl.formatMessage(messages.showLess)
              : intl.formatMessage(messages.showMore, { count: hiddenCount })}
          </Button>
        ) : null}
      </div>
    </div>
  );
};

const mapDispatchToProps = {
  onSentEmailEntitiesFetch: fetchSentEmailEntities,
};

export default connect(null, mapDispatchToProps)(SentEmailRecipients);
